import React, { useState, useRef } from 'react';
import { DashboardLayout } from './DashboardLayout';
import { Button } from '../ui';
import { BookOpenIcon, PlusIcon, SparklesIcon, CheckCircle2Icon, PlayCircleIcon, FileTextIcon, ClockIcon, SearchIcon, GripVerticalIcon, ArrowUpIcon, ArrowDownIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, PrinterIcon, DownloadIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type Module = {
  id: string;
  title: string;
  duration: string;
  format: string;
  completed: boolean;
};
type Slide = {
  title: string;
  body: string;
  bullets?: string[];
};
type Handout = {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
};
const MODULE_CONTENT: Record<string, {
  slides: Slide[];
  handout: Handout;
}> = {
  m1: {
    slides: [{
      title: 'Leadership Reset',
      body: 'A 3-hour facilitated workshop for leaders to recalibrate operating norms after a period of change.',
      bullets: ['Why we are doing this — context & business case', 'How we will work together for the next 3 hours', 'What success looks like by the time we leave the room']
    }, {
      title: 'Where are we now?',
      body: 'Anonymous live poll: rate the current state of our team across 6 leadership dimensions.',
      bullets: ['Clarity of priorities', 'Speed of decisions', 'Quality of feedback', 'Trust across functions']
    }, {
      title: 'The Reset Framework',
      body: 'Three resets we make explicit in the next hour:',
      bullets: ['Reset #1 · Decision rights', 'Reset #2 · Meeting & cadence rules', 'Reset #3 · Communication norms']
    }, {
      title: 'Commitments',
      body: 'Each leader leaves with one specific operating change they own — and a peer to hold them accountable.',
      bullets: ['Write your one change', 'Pair with a peer', '30-day check-in scheduled']
    }],
    handout: {
      title: 'Leadership Reset · Participant Workbook',
      sections: [{
        heading: 'Pre-work',
        body: 'Bring one example of a recent decision that took too long. Be specific: what stalled, where it stalled, and what it cost.'
      }, {
        heading: 'Worksheet · Reset #1 — Decision Rights',
        body: 'List the top 5 decisions your team makes weekly. For each: who proposes, who decides, who is consulted, who is informed.'
      }, {
        heading: 'Worksheet · Reset #2 — Meeting & Cadence',
        body: 'Audit your current weekly load. Mark each meeting Keep / Compress / Kill.'
      }, {
        heading: 'Worksheet · Reset #3 — Communication Norms',
        body: 'Pick the lane for each: Slack, async doc, sync meeting, 1:1. Note the default response time.'
      }, {
        heading: 'Commitment Card',
        body: 'I will change ____ by ____. My accountability partner is ____. Our 30-day check-in is on ____.'
      }]
    }
  },
  m2: {
    slides: [{
      title: 'Team Trust & Vulnerability',
      body: 'A 2.5-hour interactive session that builds the muscle of saying the hard thing — kindly and early.',
      bullets: ['The cost of unsaid things', 'Vulnerability ≠ oversharing', 'How psychologically safe teams operate']
    }, {
      title: 'The 4 Levels of Trust',
      body: 'Walk through where your team sits today and what level 3 looks like.',
      bullets: ['Level 1 · Reliable', 'Level 2 · Competent', 'Level 3 · Honest', 'Level 4 · Vulnerable']
    }, {
      title: 'Practice · The Clearing Conversation',
      body: 'Paired exercise. 7 minutes each direction. Structured prompt to surface an unspoken tension.'
    }, {
      title: 'Team Norms',
      body: 'Co-create 3 explicit norms the team will hold each other to.'
    }],
    handout: {
      title: 'Team Trust & Vulnerability · Participant Workbook',
      sections: [{
        heading: 'Self-Assessment',
        body: 'Rate yourself 1–5 on each of the 4 trust levels. Where do you have a gap?'
      }, {
        heading: 'Clearing Conversation Prompt',
        body: 'Something I have been carrying that I have not said is ____. I have not said it because ____. What I would like to be true is ____.'
      }, {
        heading: 'Team Norms Worksheet',
        body: 'Draft 3 norms. Stress-test each: "Would this norm have helped us in the last hard moment we had?"'
      }]
    }
  },
  m3: {
    slides: [{
      title: 'Communication Under Pressure',
      body: 'A 2-hour workshop on staying clear, calm, and useful when the stakes are high.',
      bullets: ['How pressure distorts our default communication style', 'A pre-game routine for hard conversations', 'A framework for high-stakes messages']
    }, {
      title: 'Your Pressure Pattern',
      body: 'Self-assessment: under pressure, do you push, freeze, accommodate, or avoid?'
    }, {
      title: 'The SBI-Q Framework',
      body: 'Situation · Behavior · Impact · Question. Practice with a real recent example.'
    }, {
      title: 'Practice & Feedback',
      body: 'Role-play in triads. Observer gives one specific piece of feedback.'
    }],
    handout: {
      title: 'Communication Under Pressure · Workbook',
      sections: [{
        heading: 'Pressure Pattern Self-Quiz',
        body: '12 questions. Score yourself across the four pressure responses.'
      }, {
        heading: 'SBI-Q Worksheet',
        body: 'Pick a real recent moment. Write the Situation, the Behavior, the Impact on you, and the Question you want to ask.'
      }, {
        heading: 'Pre-Game Routine',
        body: 'My 60-second routine before a hard conversation is: 1) ____ 2) ____ 3) ____.'
      }]
    }
  },
  m4: {
    slides: [{
      title: 'Strategic Alignment Sprint',
      body: 'A 4-hour facilitated sprint to lock the next 90-day plan with no hidden disagreements.',
      bullets: ['Why most "alignment" sessions fail', 'How to make disagreement productive', 'What a real commitment looks like']
    }, {
      title: 'Top 3 priorities · silent draft',
      body: '10 minutes silent. Each leader writes their top 3 for the next 90 days.'
    }, {
      title: 'Surface the disagreement',
      body: 'Cluster, then debate the deltas. The disagreement IS the work.'
    }, {
      title: 'Lock & Commit',
      body: 'One sheet, signed: priorities, owners, success metrics, what we are saying NO to.'
    }],
    handout: {
      title: 'Strategic Alignment Sprint · Workbook',
      sections: [{
        heading: 'Silent Draft',
        body: 'My top 3 priorities for the next 90 days are: 1) ____ 2) ____ 3) ____.'
      }, {
        heading: 'Delta Map',
        body: 'Where do my top 3 differ from the team draft? What is the disagreement actually about?'
      }, {
        heading: 'Commitment Sheet',
        body: 'Priority · Owner · Metric · "What we are NOT doing because of this".'
      }]
    }
  }
};
const LIBRARY_DETAILS: Record<string, {
  duration: string;
  format: string;
}> = {
  'Communication Under Pressure': {
    duration: '2 hours',
    format: 'Workshop'
  },
  'Strategic Alignment': {
    duration: '4 hours',
    format: 'Facilitated'
  },
  'Feedback Culture': {
    duration: '1.5 hours',
    format: 'Workshop'
  },
  'Customer Excellence': {
    duration: '2 hours',
    format: 'Workshop'
  }
};
export function LearningPage() {
  const [activeModules, setActiveModules] = useState<Module[]>([{
    id: 'm1',
    title: 'Leadership Reset',
    duration: '3 hours',
    format: 'Workshop',
    completed: true
  }, {
    id: 'm2',
    title: 'Team Trust & Vulnerability',
    duration: '2.5 hours',
    format: 'Interactive',
    completed: false
  }, {
    id: 'm3',
    title: 'Communication Under Pressure',
    duration: '2 hours',
    format: 'Workshop',
    completed: false
  }, {
    id: 'm4',
    title: 'Strategic Alignment Sprint',
    duration: '4 hours',
    format: 'Facilitated',
    completed: false
  }]);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [slidesFor, setSlidesFor] = useState<Module | null>(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [handoutFor, setHandoutFor] = useState<Module | null>(null);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryFlash, setLibraryFlash] = useState<string | null>(null);
  const pathwayRef = useRef<HTMLDivElement>(null);
  const libraryRef = useRef<HTMLDivElement>(null);
  const librarySearchRef = useRef<HTMLInputElement>(null);
  const handoutPrintRef = useRef<HTMLDivElement>(null);
  const libraryModules = [{
    title: 'Communication Under Pressure',
    category: 'Leadership',
    duration: '2h',
    rating: 4.8
  }, {
    title: 'Strategic Alignment',
    category: 'Strategy',
    duration: '4h',
    rating: 4.9
  }, {
    title: 'Feedback Culture',
    category: 'Culture',
    duration: '1.5h',
    rating: 4.7
  }, {
    title: 'Customer Excellence',
    category: 'Operations',
    duration: '2h',
    rating: 4.6
  }];
  const filteredLibrary = libraryModules.filter(m => m.title.toLowerCase().includes(librarySearch.toLowerCase()) || m.category.toLowerCase().includes(librarySearch.toLowerCase()));
  const reorder = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= activeModules.length) return;
    const next = [...activeModules];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setActiveModules(next);
  };
  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const fromIdx = activeModules.findIndex(m => m.id === dragId);
    const toIdx = activeModules.findIndex(m => m.id === targetId);
    reorder(fromIdx, toIdx);
    setDragId(null);
    setDragOverId(null);
  };
  const scrollToPathway = () => {
    pathwayRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToLibrary = () => {
    libraryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    setTimeout(() => librarySearchRef.current?.focus(), 400);
  };
  const openSlides = (mod: Module) => {
    setSlidesFor(mod);
    setSlideIdx(0);
  };
  const openHandout = (mod: Module) => setHandoutFor(mod);
  const handlePrint = () => {
    const node = handoutPrintRef.current;
    if (!node || !handoutFor) return;
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) return;
    const title = handoutFor.title + ' · Handout';
    const css = ['* { box-sizing: border-box; }', 'body { font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #0f172a; line-height: 1.55; margin: 0; padding: 48px; max-width: 720px; }', 'h1 { font-size: 24px; margin: 0 0 6px; }', 'h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.1em; color: #64748b; margin: 24px 0 8px; }', 'h3 { font-size: 16px; margin: 18px 0 6px; color: #0B1B3B; }', 'p { margin: 0 0 12px; font-size: 14px; }', '.meta { font-size: 12px; color: #64748b; margin-bottom: 24px; }', '.sec { padding: 16px 0; border-top: 1px solid #e2e8f0; }', '@media print { body { padding: 24px; } }'].join('\n');
    const html = '<!doctype html><html><head><title>' + title + '</title><style>{`' + css + '`}</style></head><body>' + node.innerHTML + '</body></html>';
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  };
  const addFromLibrary = (title: string) => {
    if (activeModules.some(m => m.title === title)) {
      // Flash & scroll
      setLibraryFlash(title);
      setTimeout(() => setLibraryFlash(null), 1500);
      return;
    }
    const details = LIBRARY_DETAILS[title] || {
      duration: '2 hours',
      format: 'Workshop'
    };
    setActiveModules(prev => [...prev, {
      id: `m${Date.now()}`,
      title,
      duration: details.duration,
      format: details.format,
      completed: false
    }]);
    setLibraryFlash(title);
    setTimeout(() => setLibraryFlash(null), 1500);
    setTimeout(scrollToPathway, 100);
  };
  const slidesContent = slidesFor ? MODULE_CONTENT[slidesFor.id] : null;
  return <DashboardLayout pageTitle="Learning & Content" breadcrumb="Leadership Reset Retreat" data-id="element-1527">
      <div className="mb-8 bg-gradient-to-r from-brand-navy to-slate-800 rounded-2xl p-8 text-white relative overflow-hidden shadow-soft" data-id="element-1528">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-teal/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-1529" />
        <div className="relative z-10 max-w-2xl" data-id="element-1530">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1 text-xs font-bold text-teal-300 uppercase tracking-wider mb-4" data-id="element-1531">
            <SparklesIcon className="w-3.5 h-3.5" data-id="element-1532" /> AI Pathway Generated
          </div>
          <h2 className="text-3xl font-bold mb-3" data-id="element-1533">
            Your custom learning pathway is ready.
          </h2>
          <p className="text-slate-300 mb-6 text-lg" data-id="element-1534">
            Based on your goals to improve cross-functional trust and reset
            leadership norms, we've curated a specific sequence of modules.
          </p>
          <div className="flex gap-3" data-id="element-1535">
            <Button variant="accent" onClick={scrollToPathway} data-id="element-1536">
              Review Pathway
            </Button>
            <Button variant="outline" className="text-white border-white/20 hover:bg-white/10" onClick={scrollToLibrary} data-id="element-1537">
              Browse Library
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8" data-id="element-1538">
        {/* Active Pathway */}
        <div ref={pathwayRef} className="lg:col-span-2 space-y-6 scroll-mt-24" data-id="element-1539">
          <div className="flex items-center justify-between" data-id="element-1540">
            <div data-id="element-1541">
              <h3 className="text-xl font-bold text-brand-navy" data-id="element-1542">
                Pathway Builder
              </h3>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-1543">
                Drag to reorder modules — sequence drives the agenda.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={scrollToLibrary} data-id="element-1544">
              <PlusIcon className="w-4 h-4 mr-1.5" data-id="element-1545" /> Add Module
            </Button>
          </div>

          <div className="space-y-3" data-id="element-1546">
            {activeModules.map((mod, i) => {
            const isDragging = dragId === mod.id;
            const isDragOver = dragOverId === mod.id && dragId !== mod.id;
            return <div key={mod.id} draggable onDragStart={() => setDragId(mod.id)} onDragOver={e => {
              e.preventDefault();
              setDragOverId(mod.id);
            }} onDragLeave={() => setDragOverId(null)} onDrop={e => {
              e.preventDefault();
              handleDrop(mod.id);
            }} onDragEnd={() => {
              setDragId(null);
              setDragOverId(null);
            }} className={`bg-white border rounded-xl p-5 shadow-sm flex gap-4 group transition-all ${isDragging ? 'opacity-40 border-brand-teal' : isDragOver ? 'border-brand-teal ring-2 ring-brand-teal/20 -translate-y-0.5' : 'border-slate-200 hover:border-brand-teal/50'}`} data-id="element-1547">
                  <div className="shrink-0 flex items-start gap-3 pt-1" data-id="element-1548">
                    <button className="text-slate-300 hover:text-brand-navy cursor-grab active:cursor-grabbing" aria-label="Drag to reorder" data-id="element-1549">
                      <GripVerticalIcon className="w-5 h-5" data-id="element-1550" />
                    </button>
                    {mod.completed ? <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-1551">
                        <CheckCircle2Icon className="w-5 h-5" data-id="element-1552" />
                      </div> : <div className="w-8 h-8 rounded-full border-2 border-slate-300 text-slate-400 flex items-center justify-center font-bold text-sm" data-id="element-1553">
                        {i + 1}
                      </div>}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1554">
                    <div className="flex justify-between items-start mb-2 gap-3" data-id="element-1555">
                      <h4 className="text-lg font-bold text-brand-navy truncate" data-id="element-1556">
                        {mod.title}
                      </h4>
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md shrink-0" data-id="element-1557">
                        {mod.format}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4" data-id="element-1558">
                      <span className="flex items-center gap-1.5" data-id="element-1559">
                        <ClockIcon className="w-4 h-4" data-id="element-1560" /> {mod.duration}
                      </span>
                      <span className="flex items-center gap-1.5" data-id="element-1561">
                        <BookOpenIcon className="w-4 h-4" data-id="element-1562" /> Facilitator Guide
                        Ready
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center" data-id="element-1563">
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => openSlides(mod)} data-id="element-1564">
                        <PlayCircleIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1565" /> View
                        Slides
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => openHandout(mod)} data-id="element-1566">
                        <FileTextIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1567" /> Print
                        Handouts
                      </Button>
                      <div className="ml-auto flex items-center gap-1" data-id="element-1568">
                        <button onClick={() => reorder(i, i - 1)} disabled={i === 0} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-brand-navy disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center" aria-label="Move up" data-id="element-1569">
                          <ArrowUpIcon className="w-3.5 h-3.5" data-id="element-1570" />
                        </button>
                        <button onClick={() => reorder(i, i + 1)} disabled={i === activeModules.length - 1} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-brand-navy disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center" aria-label="Move down" data-id="element-1571">
                          <ArrowDownIcon className="w-3.5 h-3.5" data-id="element-1572" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>;
          })}

            <button onClick={scrollToLibrary} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 font-medium hover:bg-slate-50 hover:text-brand-navy hover:border-slate-300 transition-colors flex items-center justify-center gap-2" data-id="element-1573">
              <PlusIcon className="w-5 h-5" data-id="element-1574" /> Add Module to Pathway
            </button>
          </div>
        </div>

        {/* Library Sidebar */}
        <div ref={libraryRef} className="scroll-mt-24" data-id="element-1575">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-id="element-1576">
            <h3 className="text-lg font-bold text-brand-navy mb-4" data-id="element-1577">
              Content Library
            </h3>
            <div className="relative mb-6" data-id="element-1578">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1579" />
              <input ref={librarySearchRef} type="text" value={librarySearch} onChange={e => setLibrarySearch(e.target.value)} placeholder="Search modules..." className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal" data-id="element-1580" />
            </div>

            <div className="space-y-3" data-id="element-1581">
              {filteredLibrary.length === 0 && <div className="text-xs text-slate-400 py-3 text-center" data-id="element-1582">
                  No modules match "{librarySearch}".
                </div>}
              {filteredLibrary.map((mod, i) => {
              const inPathway = activeModules.some(m => m.title === mod.title);
              const flashing = libraryFlash === mod.title;
              return <button key={i} type="button" onClick={() => addFromLibrary(mod.title)} className={`w-full text-left p-3 rounded-lg border transition-colors group ${flashing ? 'border-brand-teal bg-brand-teal/10' : 'border-slate-100 hover:border-brand-teal/30 hover:bg-brand-teal/5'}`} data-id="element-1583">
                    <div className="flex items-center justify-between mb-1" data-id="element-1584">
                      <div className="text-xs font-semibold text-brand-teal" data-id="element-1585">
                        {mod.category}
                      </div>
                      {inPathway ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full" data-id="element-1586">
                          <CheckCircle2Icon className="w-3 h-3" data-id="element-1587" /> In pathway
                        </span> : <span className="text-[10px] font-bold text-slate-400 group-hover:text-brand-coral uppercase tracking-wider" data-id="element-1588">
                          + Add
                        </span>}
                    </div>
                    <h4 className="font-bold text-sm text-slate-900 mb-1" data-id="element-1589">
                      {mod.title}
                    </h4>
                    <div className="flex justify-between items-center text-xs text-slate-500" data-id="element-1590">
                      <span data-id="element-1591">{mod.duration}</span>
                      <span className="flex items-center gap-1" data-id="element-1592">
                        ★ {mod.rating}
                      </span>
                    </div>
                  </button>;
            })}
            </div>

            <Button variant="outline" className="w-full mt-4" onClick={() => {
            setLibrarySearch('');
            librarySearchRef.current?.focus();
          }} data-id="element-1593">
              View Full Library
            </Button>
          </div>
        </div>
      </div>

      {/* Slides modal */}
      <AnimatePresence data-id="element-1594">
        {slidesFor && slidesContent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={() => setSlidesFor(null)} data-id="element-1595">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()} data-id="element-1596">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between" data-id="element-1597">
                <div className="flex items-center gap-2 min-w-0" data-id="element-1598">
                  <PlayCircleIcon className="w-4 h-4 text-brand-coral shrink-0" data-id="element-1599" />
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 truncate" data-id="element-1600">
                    {slidesFor.title} · Slide {slideIdx + 1} /{' '}
                    {slidesContent.slides.length}
                  </div>
                </div>
                <button onClick={() => setSlidesFor(null)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-id="element-1601">
                  <XIcon className="w-4 h-4" data-id="element-1602" />
                </button>
              </div>

              <div className="bg-slate-900 aspect-[16/9] relative flex items-center justify-center" data-id="element-1603">
                <AnimatePresence mode="wait" data-id="element-1604">
                  <motion.div key={slideIdx} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.25
              }} className="text-white p-10 md:p-16 w-full h-full flex flex-col justify-center" data-id="element-1605">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3" data-id="element-1606">
                      Slide {slideIdx + 1}
                    </div>
                    <h2 className="font-serif-display text-3xl md:text-4xl font-bold mb-4 leading-tight" data-id="element-1607">
                      {slidesContent.slides[slideIdx].title}
                    </h2>
                    <p className="text-base md:text-lg text-slate-300 mb-5 max-w-3xl leading-relaxed" data-id="element-1608">
                      {slidesContent.slides[slideIdx].body}
                    </p>
                    {slidesContent.slides[slideIdx].bullets && <ul className="space-y-2 max-w-2xl" data-id="element-1609">
                        {slidesContent.slides[slideIdx].bullets!.map((b, i) => <li key={i} className="flex items-start gap-3 text-base text-slate-200" data-id="element-1610">
                            <span className="text-brand-coral mt-1" data-id="element-1611">›</span>
                            <span data-id="element-1612">{b}</span>
                          </li>)}
                      </ul>}
                  </motion.div>
                </AnimatePresence>

                {/* prev/next */}
                <button onClick={() => setSlideIdx(i => Math.max(0, i - 1))} disabled={slideIdx === 0} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1613">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-1614" />
                </button>
                <button onClick={() => setSlideIdx(i => Math.min(slidesContent.slides.length - 1, i + 1))} disabled={slideIdx === slidesContent.slides.length - 1} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1615">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-1616" />
                </button>
              </div>

              <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50" data-id="element-1617">
                <div className="flex items-center gap-1" data-id="element-1618">
                  {slidesContent.slides.map((_, i) => <button key={i} onClick={() => setSlideIdx(i)} className={`h-1.5 rounded-full transition-all ${i === slideIdx ? 'bg-brand-coral w-6' : 'bg-slate-300 hover:bg-slate-400 w-2'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-1619" />)}
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
              setSlidesFor(null);
              openHandout(slidesFor);
            }} data-id="element-1620">
                  <FileTextIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1621" /> Open Handout
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Handout modal */}
      <AnimatePresence data-id="element-1622">
        {handoutFor && MODULE_CONTENT[handoutFor.id] && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={() => setHandoutFor(null)} data-id="element-1623">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()} data-id="element-1624">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50" data-id="element-1625">
                <div className="flex items-center gap-2 min-w-0" data-id="element-1626">
                  <FileTextIcon className="w-4 h-4 text-brand-coral shrink-0" data-id="element-1627" />
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 truncate" data-id="element-1628">
                    Handout Preview · {handoutFor.title}
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-1629">
                  <Button variant="accent" size="sm" onClick={handlePrint} data-id="element-1630">
                    <PrinterIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1631" /> Print
                  </Button>
                  <button onClick={() => setHandoutFor(null)} className="w-8 h-8 rounded-full hover:bg-slate-200 flex items-center justify-center" data-id="element-1632">
                    <XIcon className="w-4 h-4" data-id="element-1633" />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto p-8 md:p-10" data-id="element-1634">
                <div ref={handoutPrintRef} data-id="element-1635">
                  <h1 className="font-serif-display text-2xl md:text-3xl font-bold text-brand-navy mb-1" data-id="element-1636">
                    {MODULE_CONTENT[handoutFor.id].handout.title}
                  </h1>
                  <div className="meta text-xs text-slate-500 mb-6" data-id="element-1637">
                    {handoutFor.format} · {handoutFor.duration} · Offsite+
                    Learning
                  </div>
                  {MODULE_CONTENT[handoutFor.id].handout.sections.map((sec, i) => <div key={i} className="sec border-t border-slate-200 pt-4 mt-4" data-id="element-1638">
                        <h3 className="text-sm font-bold text-brand-navy mb-1.5" data-id="element-1639">
                          {sec.heading}
                        </h3>
                        <p className="text-sm text-slate-700 leading-relaxed" data-id="element-1640">
                          {sec.body}
                        </p>
                      </div>)}
                </div>
              </div>

              <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50" data-id="element-1641">
                <div className="text-xs text-slate-500" data-id="element-1642">
                  Print opens in a new clean window for paper handouts.
                </div>
                <Button variant="ghost" size="sm" onClick={handlePrint} className="text-xs" data-id="element-1643">
                  <DownloadIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1644" />
                  Save as PDF
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </DashboardLayout>;
}